import { Skeleton, Stack, TableCell, TableRow } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Tag, Text, api, getQueryProps } from 'common';

import { NotableTable, useTableState } from '../../../../../../../components/Tables';
import { FlowRunLink } from '../../../../../../../components/links/FlowRunLink.tsx';
import { formatDateTimeShort } from '../../../../../../../utils/time.ts';
import { EntityCellContent } from '../../../../../Details/Flow/EntityCellContent.tsx';
import { FLOW_RUN_TAG_PROPS } from '../../../../Runs/Run/Status.tsx';
import { useFlowRunContext } from '../../../../Runs/Run/contexts/flowRunContext.ts';
import { useReactFlowViewContext } from '../../../context/context.tsx';
import type { OutputContentProps } from '../../types';
import { DefaultReturnValueContent } from '../Default/OutputContent';

export function SubFlowNodeOutputContent(
  props: OutputContentProps<api.FlowDefinition.ProducedType.Any>
) {
  const { metadata, isFullHeightView } = props;
  const returnValue: unknown = props.returnValue;
  const { selectedNodeId: flowDefinitionBlockId } = useReactFlowViewContext();
  const { flowRun } = useFlowRunContext();

  const { pagination, page, pageSize, pageOffset } = useTableState({
    localStorageKeySuffix: 'SubFlowNodeOutputContent',
    defaultPageSize: 5,
  });

  const {
    data: childFlowRuns,
    isSuccess,
    isLoading,
  } = useQuery({
    ...getQueryProps(api.client.FlowRun, 'findAll', {
      parentId: flowRun.id,
      parentFlowDefinitionBlockId: flowDefinitionBlockId,
    }),
  });
  const childFlowRunIds = childFlowRuns?.map((childFlowRun) => childFlowRun.id) ?? [];

  const paginatedFlowRuns = childFlowRuns?.slice(pageOffset, (page + 1) * pageSize);

  if (isLoading) {
    return <Skeleton variant="rectangular" />;
  }

  return (
    <Stack spacing={2}>
      <Text variant="heading5" bold>
        Subflow{childFlowRunIds.length > 1 ? 's' : ''}
      </Text>
      {!isSuccess && (
        // Use default
        <DefaultReturnValueContent
          returnValue={returnValue}
          metadata={metadata}
          isFullHeightView={isFullHeightView}
        />
      )}
      {isSuccess && (
        <NotableTable
          estimatedRowCount={childFlowRuns?.length ?? 0}
          pagination={pagination}
          isPending={isLoading}
          headerRow={
            <TableRow>
              <TableCell>Link</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Entity</TableCell>
            </TableRow>
          }
        >
          {paginatedFlowRuns?.map((childFlowRun) => (
            <TableRow key={childFlowRun.id}>
              <TableCell>
                <FlowRunLink flowRunId={childFlowRun.id} />
              </TableCell>
              <TableCell>
                <Tag {...FLOW_RUN_TAG_PROPS[childFlowRun.state]}>{childFlowRun.state}</Tag>
              </TableCell>
              <TableCell>
                {formatDateTimeShort(childFlowRun.created, {
                  precision: 'seconds',
                })}
              </TableCell>
              <TableCell>
                <EntityCellContent flowRun={childFlowRun} />
              </TableCell>
            </TableRow>
          ))}
        </NotableTable>
      )}
      <Text bold>Raw:</Text>

      <DefaultReturnValueContent
        returnValue={returnValue}
        metadata={metadata}
        isFullHeightView={isFullHeightView}
      />
    </Stack>
  );
}
